'use client';

import { c as _c } from "react/compiler-runtime";
import { ModelSavingsScopeBar } from '@/domains/pages/model-savings/components/model-savings-scope-bar';
import { SavingsGlobalDrawer, SavingsTaskDrawer } from '@/domains/pages/model-savings/components/savings-details-drawer';
import { SavingsOpportunitiesListDrawer } from '@/domains/pages/model-savings/components/savings-opportunities-list-drawer';
import { SavingsOpportunityCard } from '@/domains/pages/model-savings/components/savings-opportunity-card';
import { SavingsTaskTable } from '@/domains/pages/model-savings/components/savings-task-table';
import { TopOpportunitiesCard } from '@/domains/pages/model-savings/components/top-opportunities-card';
import { useModelSavingsPageData, type ModelSavingsView } from '@/domains/pages/model-savings/hooks/use-model-savings-page-data';
import { ModelSavingsChartsLoadingSkeleton } from '@/domains/pages/model-savings/model-savings-page-loading';
import { buildNoSegmentsEmptyReason } from '@/domains/pages/ai-proficiency/utils/segment-empty-state';
import { useAiProficiencyShellLoading } from '@/domains/pages/ai-proficiency/hooks/use-ai-proficiency-shell-loading';
import { shouldHideNebulyPageBody, useSyncNebulyPageViewState } from '@/domains/pages/hooks/use-sync-nebuly-page-view-state';
import { useNebulyPageTitleSubject } from '@/domains/pages/hooks/use-nebuly-page-title-subject';
import type { SavingsTask } from '@/domains/pages/model-savings/types';
import type { NebulyPageBodyProps } from '@/domains/pages/types';
import { useCreateQueryStringUrl } from '@/hooks/use-create-query-string-url';
import { useProjectsContext } from '@/providers/projects-provider';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
function parseModelSavingsView(value: string | null): ModelSavingsView {
  if (value === 'team' || value === 'market' || value === 'tenure') return value;
  return 'all';
}
export function ModelSavingsPageBody(t0) {
  const $ = _c(75);
  const {
    pageKey
  } = t0;
  const {
    projectId
  } = useProjectsContext();
  const searchParams = useSearchParams();
  const router = useRouter();
  const createQueryStringUrl = useCreateQueryStringUrl();
  const view = parseModelSavingsView(searchParams.get("view"));
  const segmentFromUrl = searchParams.get("segment") ?? undefined;
  const data = useModelSavingsPageData({
    projectId,
    view,
    selectedSegmentName: segmentFromUrl
  });
  useNebulyPageTitleSubject(view !== "all" && data.selectedSegmentName ? data.formatSegmentName(data.selectedSegmentName) : undefined);
  const [globalDrawerOpen, setGlobalDrawerOpen] = useState(false);
  const [listDrawerOpen, setListDrawerOpen] = useState(false);
  const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [pinnedDrawerTask, setPinnedDrawerTask] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [tableTasks, setTableTasks] = useState(t1);
  useEffect(() => {
    if (view === "all" || data.segments.length === 0 || segmentFromUrl) {
      return;
    }
    const defaultName = data.segments[0]?.name;
    if (!defaultName) {
      return;
    }
    router.replace(createQueryStringUrl({
      params: {
        segment: defaultName
      }
    }));
  }, [createQueryStringUrl, data.segments, router, segmentFromUrl, view]);
  let t2;
  if ($[1] !== createQueryStringUrl || $[2] !== router) {
    t2 = nextView => {
      if (nextView === "all") {
        router.replace(createQueryStringUrl({
          params: {
            view: "all"
          },
          toRemoveParams: ["segment"]
        }));
        return;
      }
      router.replace(createQueryStringUrl({
        params: {
          view: nextView
        },
        toRemoveParams: ["segment"]
      }));
    };
    $[1] = createQueryStringUrl;
    $[2] = router;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleViewChange = t2;
  let t3;
  if ($[4] !== createQueryStringUrl || $[5] !== router) {
    t3 = name => {
      router.replace(createQueryStringUrl({
        params: {
          segment: name
        }
      }));
    };
    $[4] = createQueryStringUrl;
    $[5] = router;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleSelectSegment = t3;
  let t4;
  if ($[7] !== data.topOpportunitiesTasks || $[8] !== pinnedDrawerTask || $[9] !== tableTasks) {
    const tasksById = new Map();
    for (const task of data.topOpportunitiesTasks) {
      tasksById.set(task.id, task);
    }
    for (const task_0 of tableTasks) {
      tasksById.set(task_0.id, task_0);
    }
    if (pinnedDrawerTask) {
      tasksById.set(pinnedDrawerTask.id, pinnedDrawerTask);
    }
    t4 = Array.from(tasksById.values());
    $[7] = data.topOpportunitiesTasks;
    $[8] = pinnedDrawerTask;
    $[9] = tableTasks;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const drawerTasks = t4;
  let t5;
  if ($[11] !== data.topOpportunitiesTasks || $[12] !== tableTasks) {
    t5 = taskId => {
      const task_1 = data.topOpportunitiesTasks.find(item_0 => item_0.id === taskId) ?? tableTasks.find(item => item.id === taskId) ?? null;
      if (task_1) {
        setPinnedDrawerTask(task_1);
      }
      setSelectedTaskId(taskId);
      setTaskDrawerOpen(true);
    };
    $[11] = data.topOpportunitiesTasks;
    $[12] = tableTasks;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleOpenTask = t5;
  let t6;
  if ($[14] !== drawerTasks) {
    t6 = taskId_0 => {
      setSelectedTaskId(taskId_0);
      const task_2 = drawerTasks.find(item_1 => item_1.id === taskId_0);
      if (task_2) {
        setPinnedDrawerTask(task_2);
      }
    };
    $[14] = drawerTasks;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleSelectedTaskIdChange = t6;
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = open => {
      setTaskDrawerOpen(open);
    };
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleTaskDrawerOpenChange = t7;
  let t8;
  let t9;
  if ($[17] !== taskDrawerOpen) {
    t8 = () => {
      if (taskDrawerOpen) {
        return;
      }
      const timeoutId = window.setTimeout(() => {
        setPinnedDrawerTask(null);
        setSelectedTaskId(null);
      }, 300);
      return () => window.clearTimeout(timeoutId);
    };
    t9 = [taskDrawerOpen];
    $[17] = taskDrawerOpen;
    $[18] = t8;
    $[19] = t9;
  } else {
    t8 = $[18];
    t9 = $[19];
  }
  useEffect(t8, t9);
  const isNoSegmentsEmpty = !data.isInitialLoading && !data.isChartsLoading && !data.isError && view !== "all" && data.segments.length === 0;
  const isGenericEmpty = !data.isInitialLoading && !data.isChartsLoading && !data.isError && data.tasksTotal === 0;
  const showPageShellLoading = useAiProficiencyShellLoading(pageKey, data.isInitialLoading, data.isError);
  const t10 = isNoSegmentsEmpty || isGenericEmpty;
  const t11 = isNoSegmentsEmpty ? buildNoSegmentsEmptyReason(view) : undefined;
  let t12;
  if ($[20] !== data.error || $[21] !== data.isError || $[22] !== data.refetch || $[23] !== showPageShellLoading || $[24] !== t10 || $[25] !== t11) {
    t12 = {
      isLoading: showPageShellLoading,
      isError: data.isError,
      error: data.error,
      refetch: data.refetch,
      isEmpty: t10,
      emptyReason: t11
    };
    $[20] = data.error;
    $[21] = data.isError;
    $[22] = data.refetch;
    $[23] = showPageShellLoading;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  useSyncNebulyPageViewState(t12);
  if (showPageShellLoading || data.isError) {
    return null;
  }
  if (isNoSegmentsEmpty) {
    const t13 = `nebuly-page-body-${pageKey}`;
    let t14;
    if ($[27] !== data.segments || $[28] !== data.selectedSegmentName || $[29] !== handleSelectSegment || $[30] !== handleViewChange || $[31] !== view) {
      t14 = <ModelSavingsScopeBar view={view} onViewChange={handleViewChange} segments={data.segments} selectedSegmentName={data.selectedSegmentName} onSelectSegment={handleSelectSegment} />;
      $[27] = data.segments;
      $[28] = data.selectedSegmentName;
      $[29] = handleSelectSegment;
      $[30] = handleViewChange;
      $[31] = view;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    let t15;
    if ($[33] !== t13 || $[34] !== t14) {
      t15 = <div data-testid={t13}>{t14}</div>;
      $[33] = t13;
      $[34] = t14;
      $[35] = t15;
    } else {
      t15 = $[35];
    }
    return t15;
  }
  if (shouldHideNebulyPageBody(false, false, isGenericEmpty)) {
    return null;
  }
  const t13 = `nebuly-page-body-${pageKey}`;
  let t14;
  if ($[36] !== data.segments || $[37] !== data.selectedSegmentName || $[38] !== handleSelectSegment || $[39] !== handleViewChange || $[40] !== view) {
    t14 = <ModelSavingsScopeBar view={view} onViewChange={handleViewChange} segments={data.segments} selectedSegmentName={data.selectedSegmentName} onSelectSegment={handleSelectSegment} />;
    $[36] = data.segments;
    $[37] = data.selectedSegmentName;
    $[38] = handleSelectSegment;
    $[39] = handleViewChange;
    $[40] = view;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== data || $[43] !== handleOpenTask || $[44] !== setGlobalDrawerOpen || $[45] !== setListDrawerOpen || $[46] !== setTableTasks || $[47] !== view) {
    t15 = data.isInitialLoading || data.isChartsLoading ? <ModelSavingsChartsLoadingSkeleton /> : <><div className="grid grid-cols-5 items-stretch border-b border-b-lightborder"><div className="col-span-2 flex border-r border-r-lightborder"><SavingsOpportunityCard data={data.scopeData} segmentName={view !== "all" && data.selectedSegmentName ? data.formatSegmentName(data.selectedSegmentName) : undefined} onCheckDetails={() => setGlobalDrawerOpen(true)} /></div><div className="col-span-3 flex"><TopOpportunitiesCard tasks={data.topTasks} totalSpendAnnual={data.scopeData.spendAnnual} onSeeAll={() => setListDrawerOpen(true)} onTaskClick={handleOpenTask} /></div></div>{data.tasksQueryScope ? <SavingsTaskTable queryScope={data.tasksQueryScope} onTaskClick={handleOpenTask} onTasksLoaded={setTableTasks} /> : null}</>;
    $[42] = data;
    $[43] = handleOpenTask;
    $[44] = setGlobalDrawerOpen;
    $[45] = setListDrawerOpen;
    $[46] = setTableTasks;
    $[47] = view;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== data.scopeData || $[50] !== data.tasksQueryScope || $[51] !== globalDrawerOpen || $[52] !== setGlobalDrawerOpen) {
    t16 = <SavingsGlobalDrawer open={globalDrawerOpen} onOpenChange={setGlobalDrawerOpen} data={data.scopeData} queryScope={data.tasksQueryScope} />;
    $[49] = data.scopeData;
    $[50] = data.tasksQueryScope;
    $[51] = globalDrawerOpen;
    $[52] = setGlobalDrawerOpen;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  let t17;
  if ($[54] !== data.scopeData || $[55] !== data.tasksTotal || $[56] !== data.topOpportunitiesTasks || $[57] !== handleOpenTask || $[58] !== listDrawerOpen || $[59] !== setListDrawerOpen) {
    t17 = <SavingsOpportunitiesListDrawer open={listDrawerOpen} onOpenChange={setListDrawerOpen} data={data.scopeData} tasks={data.topOpportunitiesTasks} tasksTotal={data.tasksTotal} onTaskClick={handleOpenTask} />;
    $[54] = data.scopeData;
    $[55] = data.tasksTotal;
    $[56] = data.topOpportunitiesTasks;
    $[57] = handleOpenTask;
    $[58] = listDrawerOpen;
    $[59] = setListDrawerOpen;
    $[60] = t17;
  } else {
    t17 = $[60];
  }
  let t18;
  if ($[61] !== data.tasksQueryScope || $[62] !== drawerTasks || $[63] !== handleSelectedTaskIdChange || $[64] !== handleTaskDrawerOpenChange || $[65] !== selectedTaskId || $[66] !== taskDrawerOpen) {
    t18 = <SavingsTaskDrawer open={taskDrawerOpen} onOpenChange={handleTaskDrawerOpenChange} tasks={drawerTasks} selectedTaskId={selectedTaskId} onSelectedTaskIdChange={handleSelectedTaskIdChange} queryScope={data.tasksQueryScope} />;
    $[61] = data.tasksQueryScope;
    $[62] = drawerTasks;
    $[63] = handleSelectedTaskIdChange;
    $[64] = handleTaskDrawerOpenChange;
    $[65] = selectedTaskId;
    $[66] = taskDrawerOpen;
    $[67] = t18;
  } else {
    t18 = $[67];
  }
  let t19;
  if ($[68] !== t13 || $[69] !== t14 || $[70] !== t15 || $[71] !== t16 || $[72] !== t17 || $[73] !== t18) {
    t19 = <div className="border-b border-b-lightborder" data-testid={t13}>{t14}{t15}{t16}{t17}{t18}</div>;
    $[68] = t13;
    $[69] = t14;
    $[70] = t15;
    $[71] = t16;
    $[72] = t17;
    $[73] = t18;
    $[74] = t19;
  } else {
    t19 = $[74];
  }
  return t19;
}